// Pure room-wall geometry. All model records and topology are supplied by the caller.
// There is no project, application state, DOM or asset access in this module.


// A context lasts for one synchronous query over its supplied room-wall data.
// Recreate it after geometry changes. Returned arrays are separate and frozen;
// the records still belong to the caller's input snapshot.
export function createSketchWallGeometryContext(state) {
  const records = values => Array.isArray(values)
    ? values.filter(value => value && typeof value === 'object' && !Array.isArray(value))
    : [];
  const nodes = records(state?.nodes);
  const walls = Object.freeze(records(state?.walls));
  const straightJoints = Object.freeze(records(state?.straightJoints));
  const nodesById = new Map(nodes.map(node => [node.id, node]));
  const wallsById = new Map(walls.map(wall => [wall.id, wall]));
  const wallsByNodeId = new Map();
  for (const wall of walls) {
    for (const nodeId of [wall.startNodeId, wall.endNodeId]) {
      const entries = wallsByNodeId.get(nodeId) || [];
      entries.push(wall);
      wallsByNodeId.set(nodeId, entries);
    }
  }
  const geometryContext = {
    wallsAtNode: nodeId => wallsByNodeId.get(nodeId) || [],
    allStraightJoints: () => straightJoints
  };
  const point = nodeId => {
    const node = nodesById.get(nodeId);
    return node ? { x: node.x, z: node.z } : { x: 0, z: 0 };
  };
  return Object.freeze({
    allWalls: () => walls,
    allStraightJoints: () => straightJoints,
    wallById: id => wallsById.get(id) || null,
    nodeById: id => nodesById.get(id) || null,
    corners(wall) {
      const ownedWall = wall ? wallsById.get(wall.id) : null;
      return ownedWall ? sketchWallCorners(ownedWall, point, geometryContext) : null;
    }
  });
}

export function sketchWallOtherNodeId(wall, nodeId) {
  if (!wall) return null;
  return wall.startNodeId === nodeId ? wall.endNodeId : wall.startNodeId;
}

export function sketchWallDirection(wall, getPoint) {
  const a = getPoint(wall.startNodeId);
  const b = getPoint(wall.endNodeId);
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const len = Math.hypot(dx, dz) || 1;
  return { x: dx / len, z: dz / len };
}

export function sketchLineIntersection(p1, d1, p2, d2) {
  const denom = d1.x * d2.z - d1.z * d2.x;
  if (Math.abs(denom) < 1e-9) return null; // parallel — e.g. a straight 180° pass-through
  const t = ((p2.x - p1.x) * d2.z - (p2.z - p1.z) * d2.x) / denom;
  return { x: p1.x + t * d1.x, z: p1.z + t * d1.z };
}

// Shared miters for open ends, corners and straight-through T junctions.
export function sketchWallCornerAtNode(wall, nodeId, getPoint, side, geometryContext) {
  const node = getPoint(nodeId);
  const dir = sketchWallDirection(wall, getPoint);
  const half = (Number(wall.thickness) || 0) / 2;
  const leftX = -dir.z;
  const leftZ = dir.x;
  const selfPoint = { x: node.x + side * half * leftX, z: node.z + side * half * leftZ };

  const neighbors = geometryContext.wallsAtNode(nodeId).filter(other => other.id !== wall.id);

  const joint = geometryContext.allStraightJoints().find(entry => entry.nodeId === nodeId);
  if (joint && neighbors.length === 2 && (joint.wallAId === wall.id || joint.wallBId === wall.id)) {
    // `wall` is one of the two straight-through halves. With a T-stem, its
    // measured face ends at the matching physical side of that stem, not at
    // the shared center node: a centered 200-mm stem therefore removes
    // exactly 100 mm from each half at 90°, and more at an acute angle. This
    // is what makes 5000 = 2400 + 200 + 2400 without moving the through wall.
    const throughPartnerId = joint.wallAId === wall.id ? joint.wallBId : joint.wallAId;
    const partner = neighbors.find(other => other.id === throughPartnerId);
    const stem = neighbors.find(other => other.id !== throughPartnerId);
    if (stem) {
      const wallOther = getPoint(sketchWallOtherNodeId(wall, nodeId));
      const stemOther = getPoint(sketchWallOtherNodeId(stem, nodeId));
      const throughOutX = wallOther.x - node.x;
      const throughOutZ = wallOther.z - node.z;
      const stemOutX = stemOther.x - node.x;
      const stemOutZ = stemOther.z - node.z;
      const cross = throughOutX * stemOutZ - throughOutZ * stemOutX;
      if (Math.abs(cross) > 1e-9) {
        const stemDir = sketchWallDirection(stem, getPoint);
        const stemLeftX = -stemDir.z;
        const stemLeftZ = stemDir.x;
        const stemSide = cross > 0 ? -1 : 1;
        const stemHalf = (Number(stem.thickness) || 0) / 2;
        const stemFacePoint = {
          // Center the projected stem width on this exact through-wall face.
          // Using the center node here would make acute joins asymmetric on
          // the inner/outer faces (41/241 mm instead of 141/141 mm at 45°).
          x: selfPoint.x + stemSide * stemHalf * stemLeftX,
          z: selfPoint.z + stemSide * stemHalf * stemLeftZ
        };
        return sketchLineIntersection(selfPoint, dir, stemFacePoint, stemDir) || selfPoint;
      }
    }
    // Plain Schnitt (no stem): both halves still meet flush at the cut.
    if (partner) return mitered(selfPoint, dir, node, partner, getPoint, side) || selfPoint;
    return selfPoint;
  }
  if (joint && neighbors.length === 2) {
    // `wall` is the stem — butt its side edge against the through pair's
    // shared centerline (one infinite line, since both halves are
    // collinear) instead of capping it with its own perpendicular end. The
    // resulting end face runs parallel to the through-wall, embedded to its
    // midline, which is what a clean T-butt join actually looks like.
    const throughWall = neighbors.find(other => other.id === joint.wallAId || other.id === joint.wallBId);
    if (throughWall) {
      const throughDir = sketchWallDirection(throughWall, getPoint);
      return sketchLineIntersection(selfPoint, dir, node, throughDir) || selfPoint;
    }
  }

  if (neighbors.length !== 1) return selfPoint;
  const neighbor = neighbors[0];
  return mitered(selfPoint, dir, node, neighbor, getPoint, side) || selfPoint;
}

// An optional neighbour offset also supports the host's furring geometry.
export function mitered(selfPoint, dir, node, neighbor, getPoint, side, neighborOffsetOverride) {
  const neighborDir = sketchWallDirection(neighbor, getPoint);
  const neighborOffset = Number.isFinite(neighborOffsetOverride)
    ? neighborOffsetOverride
    : (Number(neighbor.thickness) || 0) / 2;
  const neighborLeftX = -neighborDir.z;
  const neighborLeftZ = neighborDir.x;
  const neighborPoint = {
    x: node.x + side * neighborOffset * neighborLeftX,
    z: node.z + side * neighborOffset * neighborLeftZ
  };
  return sketchLineIntersection(selfPoint, dir, neighborPoint, neighborDir);
}

export function sketchWallCorners(wall, getPoint, geometryContext) {
  return {
    startInner: sketchWallCornerAtNode(wall, wall.startNodeId, getPoint, 1, geometryContext),
    endInner: sketchWallCornerAtNode(wall, wall.endNodeId, getPoint, 1, geometryContext),
    startOuter: sketchWallCornerAtNode(wall, wall.startNodeId, getPoint, -1, geometryContext),
    endOuter: sketchWallCornerAtNode(wall, wall.endNodeId, getPoint, -1, geometryContext)
  };
}
